/* eslint-disable react/require-default-props */
import React, { useCallback, useState } from 'react';
import { Editor, EditorTools } from '@progress/kendo-react-editor';
import classNames from 'classnames';
import './RichTextEditor.scss';
// Import the necessary tools from Kendo Editor
const { Bold, Italic, Underline, Link, AlignLeft, AlignCenter, AlignRight, AlignJustify, Indent, Outdent, OrderedList, UnorderedList, Undo, Redo } = EditorTools;
/**
 * Checks if the HTML content is empty of visible text
 * @param content - HTML string content
 * @returns true if content has no visible text, false otherwise
 */
const isContentEmpty = (content) => {
    if (!content || content.trim() === '') {
        return true;
    }
    // Create a temporary DOM element to parse HTML and extract text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    // Get the text content without HTML tags
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    // Check if the text content is empty or only whitespace
    return textContent.trim() === '';
};
/**
 * Rich text editor component based on Kendo UI Editor
 */
export default function RichTextEditor({ defaultValue, value, onChange, onBlur, className, disabled = false, placeholder, label, required = false, error }) {
    const [isFocused, setIsFocused] = useState(false);
    const [internalValue, setInternalValue] = useState(defaultValue || '');
    const handleChange = useCallback((event) => {
        const newValue = event.html;
        setInternalValue(newValue);
        if (onChange) {
            onChange(newValue);
        }
    }, [onChange]);
    const handleFocus = useCallback(() => {
        setIsFocused(true);
    }, []);
    const handleBlur = useCallback(() => {
        setIsFocused(false);
        if (onBlur) {
            onBlur();
        }
    }, [onBlur]);
    // Use controlled or uncontrolled value
    const contentValue = value !== undefined ? value : internalValue;
    return (React.createElement("div", { className: classNames('rich-text-editor-container', className, {
            richTextEditorFocused: isFocused,
            richTextEditorError: !!error,
            richTextEditorDisabled: disabled
        }) },
        label && (React.createElement("div", { className: "rich-text-editor-label" },
            label,
            required && React.createElement("span", { className: "rich-text-editor-required" }, "*"))),
        React.createElement("div", { className: "rich-text-editor-wrapper" },
            React.createElement(Editor, { tools: [
                    [Bold, Italic, Underline],
                    [Link],
                    [AlignLeft, AlignCenter, AlignRight, AlignJustify],
                    [Indent, Outdent],
                    [OrderedList, UnorderedList],
                    [Undo, Redo]
                ], contentStyle: {
                    height: 200
                }, value: contentValue, defaultEditMode: "div", onChange: disabled ? undefined : handleChange, onFocus: disabled ? undefined : handleFocus, onBlur: disabled ? undefined : handleBlur }),
            placeholder && isContentEmpty(contentValue) && !isFocused && (React.createElement("div", { className: "rich-text-editor-placeholder", "aria-hidden": "true" }, placeholder))),
        error && React.createElement("div", { className: "rich-text-editor-error-message" }, error)));
}
